import { Box, Typography } from '@mui/material';

import { SidePanelHeading } from './SidePanelHeading';

import { zIndex } from '@/theme';

/**
 * The sticky header a right-hand rail wears: the panel's name as the
 * library's `SidePanelHeading` (20px navy, as Planning's task and project
 * views open — 3 Nov, every right panel aligned), and the project metadata
 * underneath in the label variant.
 *
 * The rail's header is where a project states what it is — name, code,
 * customer — because the page header above it carries the project's identity
 * and its areas, and everything else had been accreting on one line there.
 * `meta` is a list rather than named fields so the next thing to state is a
 * new entry, not a new prop and a new join.
 *
 * Extracted when the Team and Billing rails would have made it a third
 * hand-copy. Every line is the drift-prone kind ADR 0013 was written about —
 * the sticky offset, the panel background that stops scrolled content showing
 * through, the divider token, the `name · code` composition — and
 * `financials/BudgetAsideHeader` had already been extracted once for exactly
 * that reason, one page earlier.
 *
 * `planning/SidePanel`'s header is deliberately NOT folded in here: it carries
 * a back button and a full-bleed divider that swallows `PanelContent`'s
 * padding, which is a different component wearing a similar hat.
 */
export type AsideHeaderProps = {
  title: string;
  /** Project name, code, customer… Empty entries are dropped; the rest join on a middot. */
  meta: (string | undefined)[];
  automationId?: string;
};

export const AsideHeader = ({ title, meta, automationId }: AsideHeaderProps) => (
  <Box
    data-automation-id={automationId}
    sx={{
      pt: 2,
      pb: 1,
      position: 'sticky',
      top: 0,
      // The panel body's own white, so the sticky header shows no box (3 Nov).
      bgcolor: 'var(--color-black-00)',
      zIndex: zIndex.stickyHeader,
    }}
  >
    <SidePanelHeading name={title} />
    <Typography
      variant="label"
      component="p"
      data-automation-id="aside-meta"
      sx={{ color: 'var(--fg-3)', mt: 0.5, mb: 0 }}
    >
      {meta.filter((entry) => entry !== undefined && entry !== '').join(' · ')}
    </Typography>
  </Box>
);
